import { useCallback } from 'react';
import type { UserSession } from 'types';
import useUserStore from 'store/useUserStore';
import { getDevImpersonateUserId, isDevUserSwitchEnabled } from 'utils/devUserSwitch';
import type { MyTxtPermission, MyTxtResource } from './permissions.types';
import { ROLE_PERMISSIONS } from './rolePermissions.config';

function isDevImpersonationActive(): boolean {
  return isDevUserSwitchEnabled() && Boolean(getDevImpersonateUserId());
}

function userPermissionsList(
  user: Pick<UserSession, 'permissions'>,
): MyTxtPermission[] {
  return user.permissions ?? [];
}

/** Holding `:write` on a resource also satisfies `:read` (matches backend). */
function writeImpliesRead(permission: MyTxtPermission): MyTxtPermission | null {
  if (permission === '*' || !permission.endsWith(':read')) {
    return null;
  }
  return `${permission.slice(0, -':read'.length)}:write` as MyTxtPermission;
}

function listSatisfies(
  held: readonly MyTxtPermission[],
  permission: MyTxtPermission,
): boolean {
  if (held.includes('*')) return true;
  if (held.includes(permission)) return true;
  const writeTwin = writeImpliesRead(permission);
  return Boolean(writeTwin && held.includes(writeTwin));
}

export function resolvePermission(
  user: Pick<UserSession, 'type' | 'permissions'>,
  permission: MyTxtPermission,
): boolean {
  if (isDevImpersonationActive()) return true;

  const explicit = userPermissionsList(user);
  if (listSatisfies(explicit, permission)) return true;

  const rolePerms: MyTxtPermission[] = ROLE_PERMISSIONS[user.type] ?? [];
  return listSatisfies(rolePerms, permission);
}

export function usePermissions(): {
  can: (permission: MyTxtPermission) => boolean;
} {
  const user = useUserStore(s => s.user);

  const can = useCallback(
    (permission: MyTxtPermission): boolean => {
      if (!user) return false;
      return resolvePermission(user, permission);
    },
    [user],
  );

  return { can };
}

export function useCanWrite(resource: MyTxtResource): boolean {
  const { can } = usePermissions();
  return can(`${resource}:write`);
}
